import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  resolveTrialAccessPresentation,
  type TrialAccessPresentation,
  type TrialAccessRole,
} from "@/lib/trialAccess";
import {
  hasCoachBillingPaidAccess,
  MEETING_PROCESSING_EVENT_TYPE,
  MANUAL_UNLIMITED_ACCESS_PROMO_CODE,
} from "@/lib/soloLeaderBilling";

import { hasCurrentSubscriptionPremiumAccess } from "@/lib/trialPremiumAccess";

const COACH_TRANSITION_LICENSE_CODE = "coach_transition_60_day";

export interface TrialAccessViewModel {
  role: TrialAccessRole;
  isLoading: boolean;
  onboardingComplete: boolean;
  primaryLeaderId: string | null;
  timeZone: string;
  canCreatePremiumActivity: boolean;
  presentation: TrialAccessPresentation;
}

interface TrialAccessRowSet {
  accountMode: string | null;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  timeZone: string;
  subscriptionStatus: string | null;
  subscriptionPeriodEnd: string | null;
  subscriptionCancelAtPeriodEnd: boolean;
  subscriptionGraceEndsAt: string | null;
  promoCodeUsed: string | null;
  primaryLeaderId: string | null;
  hasCoachConnection: boolean;
  hasPendingBillingTransfer: boolean;
  onboardingComplete: boolean;
  hasMeaningfulActivation: boolean;
  hasAllowlistedAccess: boolean;
  hasPaidIntelligenceAccess: boolean;
  hasCurrentIntelligenceAccess: boolean;
}

export function useTrialAccess({
  role,
  isPreview = false,
}: {
  role: TrialAccessRole;
  isPreview?: boolean;
}): TrialAccessViewModel {
  const { user, loading: authLoading } = useAuth();
  const [now, setNow] = useState(() => new Date());
  const query = useQuery({
    queryKey: ["trial-access", role, user?.id],
    enabled: Boolean(user?.id) && !authLoading && !isPreview,
    staleTime: 60_000,
    queryFn: () => loadTrialAccessRows(user!.id, role),
  });
  const refetchTrialAccess = query.refetch;

  const rows = query.data;
  const subscriptionStatus = rows?.subscriptionStatus ?? null;
  const hasCompAccess =
    rows?.hasAllowlistedAccess === true ||
    (rows?.promoCodeUsed === MANUAL_UNLIMITED_ACCESS_PROMO_CODE &&
      hasCurrentSubscriptionPremiumAccess({
        status: subscriptionStatus,
        currentPeriodEnd: rows.subscriptionPeriodEnd,
        paymentFailureGraceEndsAt: rows.subscriptionGraceEndsAt,
        promoCodeUsed: rows.promoCodeUsed,
        now,
      })) ||
    (subscriptionStatus === "trialing" &&
      rows?.promoCodeUsed === COACH_TRANSITION_LICENSE_CODE &&
      isFutureTimestamp(rows.subscriptionPeriodEnd, now));
  const hasExpiredTransitionAccess = isExpiredCoachTransitionAccess({
    role,
    subscriptionStatus,
    promoCodeUsed: rows?.promoCodeUsed ?? null,
    subscriptionPeriodEnd: rows?.subscriptionPeriodEnd ?? null,
    now,
  });
  const hasPaidAccess = rows
    ? !hasCompAccess &&
      (rows.hasPaidIntelligenceAccess ||
        hasCoachBillingPaidAccess({
          status: subscriptionStatus,
          currentPeriodEnd: rows.subscriptionPeriodEnd,
          cancelAtPeriodEnd: rows.subscriptionCancelAtPeriodEnd,
          paymentFailureGraceEndsAt: rows.subscriptionGraceEndsAt,
        }))
    : false;
  const isTrialEligible = rows
    ? role === "coach" || (!rows.hasCoachConnection && rows.accountMode === "solo_trial")
    : false;

  const presentation = resolveTrialAccessPresentation({
    role,
    isTrialEligible,
    trialStartedAt: rows?.trialStartedAt,
    trialEndsAt:
      rows?.trialEndsAt ??
      (hasExpiredTransitionAccess ? rows?.subscriptionPeriodEnd : null),
    timeZone: rows?.timeZone ?? "UTC",
    now,
    isPreview,
    hasPaidAccess,
    hasCompAccess,
    hasExpiredAccess: hasExpiredTransitionAccess,
    isBillingTransferPending: rows?.hasPendingBillingTransfer,
    isCoachLed: role === "leader" && rows?.hasCoachConnection,
    hasMeaningfulActivation: rows?.hasMeaningfulActivation,
  });

  useEffect(() => {
    if (!rows?.trialEndsAt) return;
    const currentMs = Date.now();
    const expiryMs = new Date(rows.trialEndsAt).getTime();
    const dayBoundaryMs = findNextTrialDayBoundary(currentMs, rows.timeZone);
    const delayMs = Math.min(
      ...[expiryMs, dayBoundaryMs]
        .filter((boundary): boundary is number => Number.isFinite(boundary) && boundary > currentMs)
        .map((boundary) => boundary - currentMs),
    );
    if (!Number.isFinite(delayMs)) return;

    const timer = window.setTimeout(() => {
      setNow(new Date());
      void refetchTrialAccess();
    }, Math.max(0, delayMs) + 25);
    return () => window.clearTimeout(timer);
  }, [now, refetchTrialAccess, rows?.timeZone, rows?.trialEndsAt]);

  return {
    role,
    isLoading: authLoading || query.isLoading,
    onboardingComplete: rows?.onboardingComplete ?? false,
    primaryLeaderId: rows?.primaryLeaderId ?? null,
    timeZone: rows?.timeZone ?? "UTC",
    canCreatePremiumActivity: resolveCanCreatePremiumActivity({
      trialState: presentation.state,
      hasCompAccess,
      hasCurrentIntelligenceAccess: rows?.hasCurrentIntelligenceAccess ?? false,
    }),
    presentation: query.isError
      ? {
          state: "unknown",
          trialStartedAt: null,
          trialEndsAt: null,
          daysRemaining: null,
          message: null,
          cta: null,
        }
      : presentation,
  };
}

async function loadTrialAccessRows(
  userId: string,
  role: TrialAccessRole,
): Promise<TrialAccessRowSet> {
  const profilePromise = supabase
    .from("profiles")
    .select("account_mode, trial_started_at, trial_ends_at, timezone")
    .eq("id", userId)
    .maybeSingle();
  const subscriptionPromise = supabase
    .from("subscriptions")
    .select("status, current_period_end, cancel_at_period_end, payment_failure_grace_ends_at, promo_code_used")
    .eq("user_id", userId)
    .maybeSingle();
  const linksPromise = role === "coach"
    ? supabase
        .from("coach_leader_links")
        .select("leader_id, created_at")
        .eq("coach_id", userId)
        .order("created_at", { ascending: true })
        .limit(1)
    : supabase
        .from("coach_leader_links")
        .select("coach_id, created_at")
        .eq("leader_id", userId)
        .limit(1);
  const ownershipPromise = role === "coach"
    ? supabase
        .from("billing_ownership_resolutions")
        .select("id")
        .eq("proposed_owner_id", userId)
        .eq("proposed_owner_type", "coach")
        .eq("status", "pending")
        .limit(1)
    : supabase
        .from("billing_ownership_resolutions")
        .select("id")
        .eq("leader_id", userId)
        .eq("status", "pending")
        .limit(1);
  const onboardingPromise = role === "coach"
    ? supabase
        .from("coach_onboarding")
        .select("onboarding_completed")
        .eq("coach_id", userId)
        .maybeSingle()
    : supabase
        .from("leader_onboarding")
        .select("leader_id")
        .eq("leader_id", userId)
        .maybeSingle();
  const activationPromise = role === "leader"
    ? supabase
        .from("meeting_processing_events")
        .select("id")
        .eq("leader_id", userId)
        .eq("event_type", MEETING_PROCESSING_EVENT_TYPE)
        .limit(1)
    : null;

  const [
    profileResult,
    subscriptionResult,
    linksResult,
    ownershipResult,
    onboardingResult,
    activationResult,
  ] = await Promise.all([
    profilePromise,
    subscriptionPromise,
    linksPromise,
    ownershipPromise,
    onboardingPromise,
    activationPromise ?? Promise.resolve({ data: [], error: null }),
  ]);

  const error =
    profileResult.error ||
    subscriptionResult.error ||
    linksResult.error ||
    ownershipResult.error ||
    onboardingResult.error ||
    activationResult.error;
  if (error) throw error;

  const profile = profileResult.data;
  const subscription = subscriptionResult.data;
  const links = linksResult.data ?? [];
  const primaryLeaderId = role === "coach"
    ? (links[0] as { leader_id?: string } | undefined)?.leader_id ?? null
    : null;
  const hasCoachConnection = role === "leader" && links.length > 0;
  const entitlementResult = role === "leader"
    ? await supabase
        .from("leader_intelligence_entitlements")
        .select("status, plan_id, current_period_end")
        .eq("leader_id", userId)
        .in("status", ["active", "trialing", "allowlisted"])
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle()
    : { data: null, error: null };
  if (entitlementResult.error) throw entitlementResult.error;
  const coachEntitlementAccessResult = role === "coach"
    ? await supabase
        .rpc("current_coach_has_intelligence_access")
        .maybeSingle()
    : { data: null, error: null };
  if (coachEntitlementAccessResult.error) throw coachEntitlementAccessResult.error;
  const entitlement = entitlementResult.data as {
    status?: string | null;
    plan_id?: string | null;
    current_period_end?: string | null;
  } | null;
  const coachEntitlementAccess = coachEntitlementAccessResult.data as {
    has_current_intelligence_access?: boolean | null;
    has_paid_intelligence_access?: boolean | null;
    has_allowlisted_access?: boolean | null;
  } | null;
  const entitlementPeriodEndMs = entitlement?.current_period_end
    ? new Date(entitlement.current_period_end).getTime()
    : null;
  const entitlementIsCurrent =
    entitlementPeriodEndMs === null || entitlementPeriodEndMs > Date.now();
  const isIntelligencePlan =
    entitlement?.plan_id === "intelligence" ||
    entitlement?.plan_id === "intelligence_solo" ||
    entitlement?.plan_id === "intelligence_plus";

  return {
    accountMode: profile?.account_mode ?? null,
    trialStartedAt: profile?.trial_started_at ?? null,
    trialEndsAt: profile?.trial_ends_at ?? null,
    timeZone: normalizeTrialTimeZone(profile?.timezone),
    subscriptionStatus: subscription?.status ?? null,
    subscriptionPeriodEnd: subscription?.current_period_end ?? null,
    subscriptionCancelAtPeriodEnd: subscription?.cancel_at_period_end ?? false,
    subscriptionGraceEndsAt: subscription?.payment_failure_grace_ends_at ?? null,
    promoCodeUsed: subscription?.promo_code_used ?? null,
    primaryLeaderId,
    hasCoachConnection,
    hasPendingBillingTransfer: (ownershipResult.data ?? []).length > 0,
    onboardingComplete: role === "coach"
      ? onboardingResult.data?.onboarding_completed === true
      : Boolean(onboardingResult.data),
    hasMeaningfulActivation: role === "coach"
      ? links.length > 0
      : (activationResult.data ?? []).length > 0,
    hasAllowlistedAccess: role === "coach"
      ? coachEntitlementAccess?.has_allowlisted_access === true
      : entitlement?.status === "allowlisted" && entitlementIsCurrent,
    hasPaidIntelligenceAccess: role === "coach"
      ? coachEntitlementAccess?.has_paid_intelligence_access === true
      : resolveHasPaidIntelligenceAccess({
          status: entitlement?.status ?? null,
          planId: entitlement?.plan_id ?? null,
          currentPeriodEnd: entitlement?.current_period_end ?? null,
        }),
    hasCurrentIntelligenceAccess: role === "coach"
      ? coachEntitlementAccess?.has_current_intelligence_access === true
      : Boolean(
          isIntelligencePlan &&
            entitlementIsCurrent &&
            (entitlement?.status === "active" ||
              entitlement?.status === "allowlisted" ||
              entitlement?.status === "trialing"),
        ),
  };
}

export function normalizeTrialTimeZone(timeZone: string | null | undefined) {
  if (!timeZone) return "UTC";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return timeZone;
  } catch {
    return "UTC";
  }
}

function isFutureTimestamp(value: string | null | undefined, now: Date) {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) && timestamp > now.getTime();
}

export function isExpiredCoachTransitionAccess({
  role,
  subscriptionStatus,
  promoCodeUsed,
  subscriptionPeriodEnd,
  now = new Date(),
}: {
  role: TrialAccessRole;
  subscriptionStatus: string | null;
  promoCodeUsed: string | null;
  subscriptionPeriodEnd: string | null;
  now?: Date;
}) {
  return Boolean(
    role === "coach" &&
      subscriptionStatus === "trialing" &&
      promoCodeUsed === COACH_TRANSITION_LICENSE_CODE &&
      subscriptionPeriodEnd &&
      !isFutureTimestamp(subscriptionPeriodEnd, now),
  );
}

export function resolveHasPaidIntelligenceAccess({
  status,
  planId,
  currentPeriodEnd,
  now = new Date(),
}: {
  status: string | null;
  planId: string | null;
  currentPeriodEnd: string | null;
  now?: Date;
}) {
  const isIntelligencePlan = planId === "intelligence"
    || planId === "intelligence_solo"
    || planId === "intelligence_plus";
  const isCurrent = !currentPeriodEnd || isFutureTimestamp(currentPeriodEnd, now);
  return status === "active" && isIntelligencePlan && isCurrent;
}

function localDateKey(timestampMs: number, timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestampMs));
}

export function findNextTrialDayBoundary(nowMs: number, timeZone: string) {
  const currentDate = localDateKey(nowMs, timeZone);
  let low = nowMs;
  let high = nowMs + 27 * 60 * 60 * 1000;
  if (localDateKey(high, timeZone) === currentDate) return Number.NaN;

  while (high - low > 1_000) {
    const midpoint = Math.floor((low + high) / 2);
    if (localDateKey(midpoint, timeZone) === currentDate) low = midpoint;
    else high = midpoint;
  }
  return high;
}

export function resolveCanCreatePremiumActivity({
  trialState,
  hasCompAccess,
  hasCurrentIntelligenceAccess,
}: {
  trialState: TrialAccessPresentation["state"];
  hasCompAccess: boolean;
  hasCurrentIntelligenceAccess: boolean;
}) {
  return trialState !== "expired" && (hasCompAccess || hasCurrentIntelligenceAccess);
}
